import { AgGridReact } from "ag-grid-react";
import { Button, notification } from "antd";
import CiqAgSearch from "components/ciq-ag-search";
import CiqGridViewWrapper from "components/ciq-grid-view-wrapper";
import { useCallback, useContext, useRef, useState } from "react";
import { calculateLogPageStats } from "components/ciq-log-page-header";
import { newEntityNotificationMessage } from "entity-app/shared-components/new-entity-notification-message";
import { useParams } from "react-router";
import { FeatureTypes } from "entity-app/constants";
import ColumnConfigViews from "components/column-config-views";
import { EFeaturePageId, ProjectPermissionEnum } from "constants/index";
import { UtilColumnConfig } from "components/column-config-views/column-config-util";
import { exportEntityLogAsCSV } from "entity-app/utils/export-entity-log-as-csv";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { getRowId } from "pages/submittals-log-page/utils";
import { InfoFeatureType } from "change-events/change-event-polling";
import { CiqIconBox } from "components/svg-icons";
import DemoTextIcon from "components/svg-icons/demo-icon";
import { useFeatureLogV2InlineEdit } from "entity-app/hooks/feature-log-v2-inline-edit";
import { useDesignPackageAgGrid } from "./hooks/design-package-ag-grid-data";
import { FilterChipComponent } from "../../entity-app/shared-components/log-render-components/filter-chip-component";
import type { FilterChipComponentRef } from "../../entity-app/shared-components/log-render-components/filter-chip-component";
import CreateDesignPackageComponent from "./components/create-design-package";
import { useDesignPackageData } from "./hooks/design-package-data";

const featureKey = "design-packages"; // URL

function DesignPackageLogPage() {
  const gridRef = useRef<AgGridReact<any>>(null);
  const {
    projectDetails,
    tokenContents,
    columnHeaders: { designPackageHeaderMap }
  } = useContext(ProjectContext);
  const cannotWriteAccessOfDesignPackage = isPermissionNotGrantted(
    ProjectPermissionEnum.DesignPackageWrite,
    tokenContents?.role!
  );

  const pageData = useDesignPackageData({ gridRef });
  const agGridData = useDesignPackageAgGrid({
    featureKey,
    milestonesColumns: pageData.milestonesColumns,
    designPackageHeader: designPackageHeaderMap,
    cannotWriteAccessOfDesignPackage
  });
  const { onCellEditRequest } = useFeatureLogV2InlineEdit({
    refetchListData: pageData.refetchDesignPackageList,
    infoFeatureType: InfoFeatureType.designPackage
  });

  const filterChipRef = useRef<FilterChipComponentRef>(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [api, contextHolder] = notification.useNotification();
  const { projectId } = useParams() as any;

  const [isFirstDataRendered, setIsFirstDataRendered] = useState(false);
  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();

  const openNotification = (id: string, title: string) => {
    api.open(
      newEntityNotificationMessage({
        projectId,
        id,
        title,
        featureKey
      })
    );
  };

  const onFirstDataRendered = useCallback(() => {
    if (
      pageData.savedColumns?.design_package_column_config &&
      gridRef.current
    ) {
      UtilColumnConfig.setFilterStateFromData({
        columnStates: pageData.savedColumns?.design_package_column_config,
        gridRef: {
          api: gridRef.current!.api
        }
      });
    }
    pageData.calculateMaxNumberOfMilestone();

    setTimeout(() => {
      // NOTE: To load initial data once in onFilterApplied method
      setIsFirstDataRendered(true);
    }, 500);
  }, [pageData]);

  const headerItems = [
    <FilterChipComponent
      key="DesignPackageLog_FilterChip"
      gridRef={gridRef}
      ref={filterChipRef}
    />,
    <CiqAgSearch key="DesignPackageLog_search" gridRef={gridRef} />,
    <ColumnConfigViews
      key="DesignPackageLog_ColumnConfigViews"
      featureId={EFeaturePageId.DESIGN_PACKAGE}
      setFilterStateFromData={(columnStates) => {
        if (gridRef.current)
          UtilColumnConfig.setFilterStateFromData({
            columnStates,
            gridRef: {
              api: gridRef.current!.api
            }
          });
      }}
      getColumnStateFromGrid={() => {
        if (gridRef.current) {
          return UtilColumnConfig.getColumnStateFromGrid({
            gridRef: {
              api: gridRef.current!.api
            }
          });
        }
        return null;
      }}
      afterApplyFilterCallBack={pageData.calculateMaxNumberOfMilestone}
    />,
    <Button key="DesignPackageLog_importLog" disabled>
      Import Log
    </Button>,

    <Button
      key="DesignPackageLog_export"
      placeholder="Export Design Package Log"
      data-testid="index-button-export"
      onClick={() => {
        if (projectDetails && gridRef.current)
          exportEntityLogAsCSV({
            entityName: "Design Package Log",
            projectDetails,
            gridRef: {
              api: gridRef.current!.api
            },
            includedColumns: ["description"],
            excludedColumns: ["_milestones", "_durations"]
          });
      }}
      disabled={!pageData.designPackageList?.design_package_list_func}
    >
      Export
    </Button>,
    <Button
      key="DesignPackageLog_new"
      onClick={() => {
        setShowDrawer(!showDrawer);
      }}
      disabled={cannotWriteAccessOfDesignPackage}
      type="primary"
      data-testid="index-button-new-design-package"
    >
      New Design Package
    </Button>
  ];

  const onEditCell = useCallback(
    (params: any) => {
      gridRef?.current?.api?.setFocusedCell(params.rowIndex, params.colKey);
      gridRef?.current?.api?.startEditingCell({
        rowIndex: params.rowIndex,
        colKey: params.colKey
      });
    },
    [gridRef]
  );

  const entityName = (
    <div className="flex items-center space-x-2">
      <CiqIconBox feature="DESIGN_PACKAGE" size={20} padding={8} />
      <div className="text-black text-xl font-semibold pl-1">
        Design Packages
      </div>
      <DemoTextIcon />
    </div>
  );

  return (
    <>
      {contextHolder}
      <CiqGridViewWrapper
        headerInfo={{
          entityName,
          titleParam: {
            title: "Design Packages",
            totalCount:
              pageData.designPackageList?.design_package_list_func?.length,
            filterStats: statsData
          },
          items: headerItems
        }}
        gridInfo={{
          gridRef,
          columnDefs: agGridData.columnDefs,
          rowData: pageData.designPackageList?.design_package_list_func,
          defaultColDef: agGridData.defaultColDef,
          rowHeight: 62,
          getRowId, // After refetch the rowdata ,It help to persist Scroll Bar
          readOnlyEdit: true,
          singleClickEdit: true,
          stopEditingWhenCellsLoseFocus: true,
          onCellEditRequest,
          onFirstDataRendered,
          onSortChanged: () => {
            if (isFirstDataRendered) pageData.onSaveColumnState();
          },
          onFilterChanged: () => {
            filterChipRef.current?.onFilterApplied();
            setStatsData(
              calculateLogPageStats({
                gridRef,
                featureTypeId: FeatureTypes.DESIGN_PACKAGE
              })
            );
            gridRef.current?.api?.deselectAll();
            if (isFirstDataRendered) pageData.onSaveColumnState();
          },
          onColumnVisible: (e) => {
            if (e.api && e.source === "toolPanelUi") {
              if (e.column) {
                agGridData.milestonesColumns.onColumnVisible(
                  e,
                  pageData.maxNumberOfMilestone
                );
              } else {
                agGridData.milestonesColumns.updateAllMileStoneInitialVisibility(
                  e.api,
                  pageData.maxNumberOfMilestone
                );
              }
              pageData.onSaveColumnState();
            }
          },
          onDragStopped: (e) => {
            const classNames = [
              "ag-header-cell-resize",
              "ag-tool-panel-horizontal-resize"
            ];
            if (e.api && !classNames.includes(e.target.className)) {
              pageData.onSaveColumnState();
            }
          },
          context: {
            onEditCell,
            isGridCellEditable: agGridData.isGridCellEditable,
            refetchLogData: pageData.refetchDesignPackageList,
            editPermission: !cannotWriteAccessOfDesignPackage
          },
          loading: !isFirstDataRendered,
          onColumnResized: (params: any) => {
            if (params.finished) {
              // User has finished resizing, do something like save column state
              pageData.onSaveColumnState();
            }
          },
          disableResizingAutoWidth: true
        }}
      />
      <CreateDesignPackageComponent
        setDrawerOpen={setShowDrawer}
        showDrawerOpen={showDrawer}
        modelTitle="Create Design Package"
        onDesignPackageCreated={(title: string, id: string) => {
          setShowDrawer(false);
          openNotification(id, title);
          pageData.refetchDesignPackageList();
        }}
      />
    </>
  );
}

export default DesignPackageLogPage;
